import { KeyboardEvent, useCallback, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';

const NOT_BROWSING = -1;

/**
 * Whether the text sits on a single visual line, i.e. it neither contains a
 * line break nor has soft-wrapped at the textarea's current width. Layout that
 * can't be measured counts as not fitting, which keeps the stricter
 * start/end-of-text rule.
 */
const fitsOnOneVisualLine = (textarea: HTMLTextAreaElement) => {
  const { lineHeight, paddingTop, paddingBottom } = getComputedStyle(textarea);
  const textHeight =
    textarea.scrollHeight - parseFloat(paddingTop) - parseFloat(paddingBottom);
  return textHeight < parseFloat(lineHeight) * 1.5;
};

interface UseMessageHistoryParams {
  /** Previously sent messages, oldest first. */
  history: string[];
  /** The input's current text. */
  message: string;
  setMessage: (message: string) => void;
}

/**
 * Shell-style recall of previously sent messages with ArrowUp / ArrowDown.
 *
 * Navigation only kicks in with the caret at the very start (ArrowUp) or end
 * (ArrowDown) of the text, so arrow keys keep moving the caret through
 * multi-line and soft-wrapped text. The one exception is a recalled message
 * that fits on one visual line and is still unedited: there is no caret travel
 * to protect, so either edge navigates in either direction and reversing
 * direction takes a single press.
 *
 * The text typed before the first ArrowUp is held in a ref and restored when
 * ArrowDown walks past the newest message.
 */
export const useMessageHistory = ({
  history,
  message,
  setMessage,
}: UseMessageHistoryParams) => {
  const browsingIndexRef = useRef(NOT_BROWSING);
  const draftRef = useRef('');

  const resetHistoryNavigation = useCallback(() => {
    browsingIndexRef.current = NOT_BROWSING;
    draftRef.current = '';
  }, []);

  // Messages added (including ones sent from outside the input), a reset
  // chat or a retry that moves a prompt to the end leave the cursor pointing
  // at the wrong message. Keyed on the prompts themselves: the array is new on
  // every streamed token, and a retry reorders it without changing its
  // length. Reacting to every change, not comparing against the history when
  // browsing began, also catches a New Chat that goes 1 → 0 → 1.
  const historyKey = history.join('\u0000');
  useEffect(() => {
    resetHistoryNavigation();
  }, [historyKey, resetHistoryNavigation]);

  /** Returns true when the key was consumed to navigate the history. */
  const handleHistoryKeyDown = (
    event: KeyboardEvent<HTMLTextAreaElement>,
  ): boolean => {
    const { key, shiftKey, altKey, ctrlKey, metaKey, nativeEvent } = event;
    const isArrowUp = key === 'ArrowUp';
    if (
      (!isArrowUp && key !== 'ArrowDown') ||
      shiftKey ||
      altKey ||
      ctrlKey ||
      metaKey ||
      nativeEvent.isComposing
    ) {
      return false;
    }

    const textarea = event.currentTarget;
    const { selectionStart, selectionEnd, value } = textarea;
    if (selectionStart !== selectionEnd) {
      return false;
    }

    const browsingIndex = browsingIndexRef.current;
    const isBrowsing = browsingIndex !== NOT_BROWSING;

    const isCaretAtStart = selectionStart === 0;
    const isCaretAtEnd = selectionStart === value.length;
    const isUneditedSingleLineRecall =
      isBrowsing &&
      value === history[browsingIndex] &&
      fitsOnOneVisualLine(textarea);
    const canNavigate = isUneditedSingleLineRecall
      ? isCaretAtStart || isCaretAtEnd
      : isArrowUp
        ? isCaretAtStart
        : isCaretAtEnd;
    if (!canNavigate) {
      return false;
    }

    let nextIndex: number;
    if (isArrowUp) {
      if (history.length === 0 || browsingIndex === 0) {
        return false;
      }
      if (!isBrowsing) {
        draftRef.current = message;
      }
      nextIndex = isBrowsing ? browsingIndex - 1 : history.length - 1;
    } else {
      if (!isBrowsing) {
        return false;
      }
      nextIndex =
        browsingIndex === history.length - 1 ? NOT_BROWSING : browsingIndex + 1;
    }

    const nextMessage =
      nextIndex === NOT_BROWSING ? draftRef.current : history[nextIndex];
    browsingIndexRef.current = nextIndex;

    event.preventDefault();
    // Commit the controlled value now so callers can measure the updated DOM
    // (auto-resize), and park the caret at the end for editing.
    flushSync(() => setMessage(nextMessage));
    textarea.setSelectionRange(nextMessage.length, nextMessage.length);
    return true;
  };

  return { handleHistoryKeyDown, resetHistoryNavigation };
};
